/**
 * Fabric.js v7 patch: high-quality image downscaling.
 *
 * Fabric sets `imageSmoothingEnabled` but never `imageSmoothingQuality`,
 * so every drawImage runs at the browser default of 'low' — one bilinear
 * tap, which only ever samples a 2x2 neighbourhood. Chrome hides that for
 * modest reductions by mipmapping decoded <img> sources, but past roughly
 * 10-15x the chain runs out: a 4000px logo drawn into a 150px box picks up
 * ~30% more edge energy than a correct area resample, which is what reads
 * as a "pixelated" logo. Cache canvases never get mipmaps at all, so a
 * cached subtree scaled during a resize drag is worse again.
 *
 * 'high' lands within a few percent of an exact area average and costs
 * nothing measurable — the raster backend already holds the mipmaps — so
 * it is set on every context Fabric paints image content into:
 *
 *   1. Image._render        — the source bitmap into its own cache, or
 *                             straight onto the target context.
 *   2. Object.drawCacheOnCanvas — a cached bitmap onto its parent, which
 *                             is where a live scale drag lands because
 *                             `noScaleCache` freezes the cache resolution.
 *
 * Both are the same hook points Fabric itself uses for
 * `imageSmoothingEnabled`, and both flow through the export path
 * (`toDataURL`) as well as the editor canvases.
 */
import { FabricImage, FabricObject } from 'fabric';

const HIGH: ImageSmoothingQuality = 'high';

const renderImage = FabricImage.prototype._render;

FabricImage.prototype._render = function (
  this: FabricImage,
  ctx: CanvasRenderingContext2D
) {
  ctx.imageSmoothingQuality = HIGH;
  renderImage.call(this, ctx);
};

const drawCacheOnCanvas = FabricObject.prototype.drawCacheOnCanvas;

FabricObject.prototype.drawCacheOnCanvas = function (
  this: ThisParameterType<typeof drawCacheOnCanvas>,
  ctx: CanvasRenderingContext2D
) {
  ctx.imageSmoothingQuality = HIGH;
  drawCacheOnCanvas.call(this, ctx);
};
